import { useTheme } from '@/context/ThemeContext';
import { behaviorAnalysisData } from '@/mocks/fizboData';

export default function DavranisAnalizi() {
  const { isDark } = useTheme();
  const data = behaviorAnalysisData;

  const maxViews = Math.max(...data.weeklyViews.map(d => d.views));

  return (
    <div className="space-y-4">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-2">
        {[
          { label: 'Haftalık Görüntülenme', value: data.totalViews, icon: 'ri-eye-line', color: 'text-sky-500', bg: 'bg-sky-50 dark:bg-sky-900/20' },
          { label: 'Tıklama Sayısı', value: data.totalClicks, icon: 'ri-cursor-line', color: 'text-green-500', bg: 'bg-green-50 dark:bg-green-900/20' },
          { label: 'Ort. Kalma Süresi', value: data.avgTimeOnListing, icon: 'ri-time-line', color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/20' },
          { label: 'Telefon Görüntüleme', value: data.phoneRevealCount, icon: 'ri-phone-line', color: 'text-violet-500', bg: 'bg-violet-50 dark:bg-violet-900/20' },
        ].map((kpi) => (
          <div key={kpi.label} className={`rounded-xl p-3 ${kpi.bg}`}>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${kpi.icon} text-sm ${kpi.color}`} />
              </div>
              <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>{kpi.label}</span>
            </div>
            <p className={`text-xl font-bold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* Weekly Bar Chart */}
      <div className={`rounded-xl p-4 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
        <div className="flex items-center justify-between mb-3">
          <p className={`text-xs font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
            Haftalık Görüntülenme
          </p>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-primary-500 inline-block" />
              <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Görüntülenme</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
              <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Tıklama</span>
            </div>
          </div>
        </div>
        <div className="flex items-end gap-1.5 h-28">
          {data.weeklyViews.map((d) => (
            <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex items-end gap-0.5 h-20">
                <div
                  className="flex-1 rounded-t-md bg-primary-500/80 transition-all duration-700"
                  style={{ height: `${(d.views / maxViews) * 100}%` }}
                />
                <div
                  className="flex-1 rounded-t-md bg-green-500/80 transition-all duration-700"
                  style={{ height: `${(d.clicks / maxViews) * 100}%` }}
                />
              </div>
              <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>{d.day}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top Photos */}
      <div>
        <p className={`text-xs font-semibold mb-3 uppercase tracking-wide ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
          En Çok Bakılan Fotoğraflar
        </p>
        <div className="space-y-2.5">
          {data.topPhotos.map((photo, idx) => (
            <div key={photo.name} className="flex items-center gap-3">
              <span className={`text-xs font-bold w-4 flex-shrink-0 ${
                idx === 0 ? 'text-amber-500' : isDark ? 'text-dark-muted' : 'text-light-muted'
              }`}>
                {idx + 1}
              </span>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs ${isDark ? 'text-dark-text' : 'text-light-text'}`}>{photo.name}</span>
                  <span className={`text-xs font-semibold ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                    {photo.views} görüntülenme
                  </span>
                </div>
                <div className={`w-full h-1.5 rounded-full ${isDark ? 'bg-dark-border' : 'bg-light-border'}`}>
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${
                      idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-primary-500' : 'bg-gray-400 dark:bg-gray-600'
                    }`}
                    style={{ width: `${photo.pct}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Insight */}
      <div className={`rounded-xl p-4 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
        <p className={`text-xs font-semibold mb-2 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
          Davranış İçgörüsü
        </p>
        <ul className="space-y-1.5">
          {[
            'Cuma günleri en yüksek ilgi - bu gün paylaşım yapın.',
            'Salon fotoğrafı en çok ilgi görüyor, daha fazla açı ekleyin.',
            `%${Math.round((data.totalClicks / data.totalViews) * 100)} tıklama oranı - sektör ortalamasının üzerinde.`,
          ].map((insight, i) => (
            <li key={i} className="flex items-start gap-2">
              <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-lightbulb-line text-xs text-amber-500" />
              </div>
              <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>{insight}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
